import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type GoogleMapReact from 'google-map-react';

import type { SearchLocationDetails, SearchSuggestion, SidebarPopulationMode } from './interface';

export const geocodeSearchSelection = ({
    label,
    maps,
}: {
    label: string;
    maps?: typeof google.maps;
}): Promise<SearchLocationDetails | null> =>
    new Promise((resolve) => {
        if (!maps) {
            resolve(null);
            return;
        }

        const geocoder = new maps.Geocoder();
        geocoder.geocode(
            {
                address: `${label}, Romania`,
                region: 'ro'
            },
            (results, status) => {
                if (status === maps.GeocoderStatus.OK && results && results.length > 0) {
                    const [firstResult] = results;
                    const location = firstResult.geometry.location;
                    resolve({
                        center: {lat: location.lat(), lng: location.lng()},
                        types: firstResult.types ?? [],
                    });
                    return;
                }

                resolve(null);
            }
        );
    });

export const fetchGoogleSuggestions = ({
    value,
    maps,
    placesAutocompleteServiceRef,
    autocompleteRequestIdRef,
    setSearchSuggestions,
    setIsSearchLoading,
    setHighlightedSuggestionIndex,
}: {
    value: string;
    maps?: typeof google.maps;
    placesAutocompleteServiceRef: MutableRefObject<google.maps.places.AutocompleteService | null>;
    autocompleteRequestIdRef: MutableRefObject<number>;
    setSearchSuggestions: Dispatch<SetStateAction<SearchSuggestion[]>>;
    setIsSearchLoading: Dispatch<SetStateAction<boolean>>;
    setHighlightedSuggestionIndex: Dispatch<SetStateAction<number>>;
}) => {
    const trimmedValue = value.trim();

    if (!maps?.places || !trimmedValue) {
        autocompleteRequestIdRef.current += 1;
        setSearchSuggestions([]);
        setIsSearchLoading(false);
        setHighlightedSuggestionIndex(-1);
        return;
    }

    const currentRequestId = autocompleteRequestIdRef.current + 1;
    autocompleteRequestIdRef.current = currentRequestId;
    setIsSearchLoading(true);

    const fallbackToLegacyAutocomplete = () => {
        if (!maps.places) {
            if (autocompleteRequestIdRef.current !== currentRequestId) {
                return;
            }

            setSearchSuggestions([]);
            setHighlightedSuggestionIndex(-1);
            setIsSearchLoading(false);
            return;
        }

        if (!placesAutocompleteServiceRef.current) {
            placesAutocompleteServiceRef.current = new maps.places.AutocompleteService();
        }

        placesAutocompleteServiceRef.current.getPlacePredictions(
            {
                input: trimmedValue,
                componentRestrictions: {country: 'ro'},
                types: ['geocode'],
            },
            (predictions, status) => {
                if (autocompleteRequestIdRef.current !== currentRequestId) {
                    return;
                }

                const isOk = status === google.maps.places.PlacesServiceStatus.OK;

                if (!isOk || !predictions) {
                    setSearchSuggestions([]);
                    setHighlightedSuggestionIndex(-1);
                    setIsSearchLoading(false);
                    return;
                }

                const nextSuggestions = predictions.map((prediction) => ({
                    id: prediction.place_id,
                    label: prediction.description,
                    placeId: prediction.place_id,
                }));

                setSearchSuggestions(nextSuggestions);
                setHighlightedSuggestionIndex(nextSuggestions.length > 0 ? 0 : -1);
                setIsSearchLoading(false);
            }
        );
    };

    void maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: trimmedValue,
        includedRegionCodes: ['ro'],
    })
        .then(({suggestions}) => {
            if (autocompleteRequestIdRef.current !== currentRequestId) {
                return;
            }

            const nextSuggestions = suggestions
                .map((suggestion) => suggestion.placePrediction)
                .filter((prediction): prediction is google.maps.places.PlacePrediction => !!prediction)
                .map((prediction) => ({
                    id: prediction.placeId,
                    label: prediction.text.text,
                    placeId: prediction.placeId,
                    placePrediction: prediction,
                }));

            setSearchSuggestions(nextSuggestions);
            setHighlightedSuggestionIndex(nextSuggestions.length > 0 ? 0 : -1);
        })
        .catch(() => {
            if (autocompleteRequestIdRef.current !== currentRequestId) {
                return;
            }

            fallbackToLegacyAutocomplete();
        })
        .finally(() => {
            if (autocompleteRequestIdRef.current !== currentRequestId) {
                return;
            }

            if (!placesAutocompleteServiceRef.current) {
                setIsSearchLoading(false);
            }
        });
};

export const runManualViewportUpdate = ({
    nextCenter,
    nextBounds,
    requestZoom,
    sidebarMode,
    skipViewportRefreshCountRef,
    requestZoomOverrideRef,
    updateMarkers,
}: {
    nextCenter: GoogleMapReact.Coords;
    nextBounds: [number, number, number, number] | undefined;
    requestZoom: number;
    sidebarMode: SidebarPopulationMode;
    skipViewportRefreshCountRef: MutableRefObject<number>;
    requestZoomOverrideRef: MutableRefObject<number | null>;
    updateMarkers: (
        search: string,
        nextCenter: GoogleMapReact.Coords,
        nextBounds: [number, number, number, number] | undefined,
        nextZoom: number,
        sidebarMode: SidebarPopulationMode
    ) => void;
}) => {
    skipViewportRefreshCountRef.current += 1;
    requestZoomOverrideRef.current = requestZoom;
    updateMarkers('', nextCenter, nextBounds, requestZoom, sidebarMode);
};

export const applySearchResult = async ({
    nextCenter,
    requestZoom,
    setCenter,
    setBounds,
    setZoom,
    setMapSearchViewport,
    runManualViewportUpdate,
}: {
    nextCenter: GoogleMapReact.Coords;
    requestZoom: number;
    setCenter: Dispatch<SetStateAction<GoogleMapReact.Coords>>;
    setBounds: Dispatch<SetStateAction<[number, number, number, number] | undefined>>;
    setZoom: Dispatch<SetStateAction<number>>;
    setMapSearchViewport: (
        nextCenter: GoogleMapReact.Coords,
        nextZoom: number,
        skippedRefreshCount?: number
    ) => Promise<{
        center: GoogleMapReact.Coords;
        bounds: [number, number, number, number];
        zoom: number;
    } | null>;
    runManualViewportUpdate: (
        nextCenter: GoogleMapReact.Coords,
        nextBounds: [number, number, number, number] | undefined,
        requestZoom: number,
        sidebarMode: SidebarPopulationMode
    ) => void;
}) => {
    const viewportResult = await setMapSearchViewport(nextCenter, requestZoom, 1);

    if (!viewportResult) {
        return false;
    }

    setCenter(viewportResult.center);
    setBounds(viewportResult.bounds);
    setZoom(viewportResult.zoom);
    runManualViewportUpdate(
        viewportResult.center,
        viewportResult.bounds,
        requestZoom,
        'cluster-order'
    );

    return true;
};
